import { useEffect, useState } from "react";
import {
  getMailFilterRules, addMailFilterRule, updateMailFilterRule, deleteMailFilterRule,
} from "../../services/supportMail";
import { IFilterRule, FilterListType, FilterMatchType } from "../../models/ISupportMail";

const MATCH_TYPE_META: Record<FilterMatchType, { label: string; placeholder: string; hint: string }> = {
  Domain:          { label: "Domain",            placeholder: "hostinger.com",       hint: "Gönderen domaini bu ya da alt domaini" },
  Address:         { label: "Adres (tam)",       placeholder: "kampanya@firma.com",  hint: "Adresin tam eşleşmesi" },
  AddressPattern:  { label: "Adres deseni",      placeholder: "noreply@*",           hint: "* joker karakteri kullanılabilir" },
  SubjectContains: { label: "Konu içeriyor",     placeholder: "domain yenileme",     hint: "Konu bu metni içeriyorsa" },
};

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

interface Props {
  onClose: () => void;
  isMobile: boolean;
}

export default function MailFilterRulesModal({ onClose, isMobile }: Props) {
  const [activeType, setActiveType] = useState<FilterListType>("Block");
  const [rules, setRules] = useState<IFilterRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [showAdd, setShowAdd] = useState(false);
  const [newMatchType, setNewMatchType] = useState<FilterMatchType>("Domain");
  const [newValue, setNewValue] = useState("");
  const [newNote, setNewNote] = useState("");
  const [addError, setAddError] = useState("");
  const [saving, setSaving] = useState(false);

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setError("");
    getMailFilterRules()
      .then((res) => setRules(res.data ?? []))
      .catch(() => setError("Kurallar yüklenemedi."))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const visibleRules = rules.filter((r) => r.listType === activeType);

  const handleAdd = async () => {
    if (!newValue.trim()) { setAddError("Değer boş olamaz."); return; }
    setSaving(true);
    setAddError("");
    try {
      await addMailFilterRule({
        listType: activeType,
        matchType: newMatchType,
        value: newValue.trim(),
        note: newNote.trim() || undefined,
      });
      setNewValue("");
      setNewNote("");
      setShowAdd(false);
      load();
    } catch (err: any) {
      const code = err?.response?.data?.[0]?.code;
      const desc = err?.response?.data?.[0]?.description;
      setAddError(
        code === "SupportMailFilterRuleAlreadyExists" ? "Bu kural zaten var." :
        code === "SupportMailFilterRuleInvalidValue" ? "Değer bu tür için geçersiz." :
        desc ?? "Kural eklenemedi."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (rule: IFilterRule) => {
    setBusyId(rule.id);
    try {
      await updateMailFilterRule({ id: rule.id, isEnabled: !rule.isEnabled });
      load();
    } catch {
      setError("Kural güncellenemedi.");
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (id: string) => {
    setBusyId(id);
    try {
      await deleteMailFilterRule(id);
      setConfirmDeleteId(null);
      load();
    } catch {
      setError("Kural silinemedi.");
    } finally {
      setBusyId(null);
    }
  };

  const panelStyle: React.CSSProperties = isMobile
    ? { position: "fixed", inset: 0, zIndex: 10001, background: "#fff", display: "flex", flexDirection: "column", overflow: "hidden" }
    : { position: "fixed", top: "50%", left: "50%", zIndex: 10001, transform: "translate(-50%,-50%)", width: "min(640px, calc(100vw - 24px))", maxHeight: "88vh", background: "#fff", borderRadius: 20, boxShadow: "0 24px 64px rgba(0,0,0,0.2)", display: "flex", flexDirection: "column", overflow: "hidden" };

  const inp: React.CSSProperties = { width: "100%", padding: "9px 12px", boxSizing: "border-box", border: "1.5px solid #e5e7eb", borderRadius: 10, fontSize: "0.85rem", outline: "none", color: "#111827" };

  return (
    <>
      {!isMobile && <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 10000, background: "rgba(0,0,0,0.5)", backdropFilter: "blur(2px)" }} />}
      <div style={panelStyle}>
        {/* Header */}
        <div style={{ padding: isMobile ? "16px 16px 14px" : "18px 22px", background: "linear-gradient(135deg, #4338ca, #3730a3)", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <i className="bi bi-funnel-fill" style={{ color: "#fff", fontSize: "1.1rem" }}></i>
            <span style={{ fontWeight: 800, fontSize: "1rem", color: "#fff" }}>Filtre Kuralları</span>
          </div>
          <button onClick={onClose} style={{ background: "rgba(255,255,255,0.15)", border: "none", borderRadius: 8, width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff", fontSize: "1.1rem" }}>×</button>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 8, padding: "12px 16px 0", flexShrink: 0 }}>
          {([["Block", "Engel Listesi", "#dc2626"], ["Allow", "Beyaz Liste", "#16a34a"]] as [FilterListType, string, string][]).map(([type, label, color]) => (
            <button key={type} onClick={() => setActiveType(type)} style={{
              flex: 1, padding: "9px 12px", borderRadius: 10, border: "1.5px solid",
              borderColor: activeType === type ? color : "#e5e7eb",
              background: activeType === type ? `${color}14` : "#fff",
              color: activeType === type ? color : "#6b7280",
              fontWeight: 700, fontSize: "0.84rem", cursor: "pointer",
            }}>
              {label} <span style={{ opacity: 0.7 }}>({rules.filter((r) => r.listType === type).length})</span>
            </button>
          ))}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
          {error && (
            <div style={{ marginBottom: 12, padding: "8px 12px", borderRadius: 9, background: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626", fontSize: "0.8rem" }}>
              <i className="bi bi-exclamation-circle-fill me-1"></i>{error}
            </div>
          )}

          {!showAdd ? (
            <button onClick={() => { setShowAdd(true); setNewMatchType("Domain"); setNewValue(""); setNewNote(""); setAddError(""); }} style={{
              width: "100%", padding: "10px", borderRadius: 10, border: "1.5px dashed #c7d2fe",
              background: "#f8faff", color: "#4338ca", fontWeight: 700, fontSize: "0.84rem", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginBottom: 14,
            }}>
              <i className="bi bi-plus-lg"></i> {activeType === "Block" ? "Engel" : "Beyaz Liste"} Kuralı Ekle
            </button>
          ) : (
            <div style={{ background: "#f8faff", border: "1.5px solid #e0e7ff", borderRadius: 14, padding: 14, marginBottom: 14 }}>
              {addError && (
                <div style={{ marginBottom: 10, padding: "7px 11px", borderRadius: 8, background: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626", fontSize: "0.78rem" }}>{addError}</div>
              )}
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 2fr", gap: 10, marginBottom: 10 }}>
                <select value={newMatchType} onChange={(e) => setNewMatchType(e.target.value as FilterMatchType)} style={{ ...inp, background: "#fff", cursor: "pointer" }}>
                  {(Object.keys(MATCH_TYPE_META) as FilterMatchType[]).map((mt) => (
                    <option key={mt} value={mt}>{MATCH_TYPE_META[mt].label}</option>
                  ))}
                </select>
                <input type="text" value={newValue} onChange={(e) => setNewValue(e.target.value)} placeholder={MATCH_TYPE_META[newMatchType].placeholder} style={inp} />
              </div>
              <div style={{ fontSize: "0.72rem", color: "#6b7280", marginBottom: 10 }}>
                <i className="bi bi-info-circle me-1"></i>{MATCH_TYPE_META[newMatchType].hint}
              </div>
              <input type="text" value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Not (opsiyonel)" style={{ ...inp, marginBottom: 12 }} />
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => setShowAdd(false)} disabled={saving} style={{ flex: 1, padding: "9px", borderRadius: 9, border: "1.5px solid #e5e7eb", background: "#fff", color: "#374151", fontWeight: 600, fontSize: "0.82rem", cursor: "pointer" }}>Vazgeç</button>
                <button onClick={handleAdd} disabled={saving} style={{ flex: 1, padding: "9px", borderRadius: 9, border: "none", background: saving ? "#e5e7eb" : "#4338ca", color: saving ? "#9ca3af" : "#fff", fontWeight: 700, fontSize: "0.82rem", cursor: saving ? "not-allowed" : "pointer" }}>
                  {saving ? "Ekleniyor..." : "Ekle"}
                </button>
              </div>
            </div>
          )}

          {loading ? (
            <div style={{ textAlign: "center", padding: "30px 0" }}><span className="spinner-border spinner-border-sm text-secondary" /></div>
          ) : visibleRules.length === 0 ? (
            <div style={{ textAlign: "center", padding: "30px 0", color: "#9ca3af" }}>
              <i className="bi bi-funnel" style={{ fontSize: "1.8rem", display: "block", marginBottom: 8, opacity: 0.4 }}></i>
              <span style={{ fontSize: "0.82rem" }}>Bu listede kural yok</span>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {visibleRules.map((rule) => (
                <div key={rule.id} style={{
                  padding: "11px 13px", borderRadius: 12,
                  background: rule.isEnabled ? "#fff" : "#f9fafb",
                  border: `1.5px solid ${rule.isEnabled ? "#f1f5f9" : "#e5e7eb"}`,
                  opacity: rule.isEnabled ? 1 : 0.65,
                }}>
                  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                        <span style={{ fontSize: "0.68rem", fontWeight: 700, color: "#6366f1", background: "#eef2ff", padding: "2px 8px", borderRadius: 20 }}>
                          {MATCH_TYPE_META[rule.matchType].label}
                        </span>
                        <span style={{ fontWeight: 700, fontSize: "0.86rem", color: "#111827", wordBreak: "break-all" }}>{rule.value}</span>
                      </div>
                      {rule.note && <div style={{ fontSize: "0.76rem", color: "#6b7280", marginTop: 4 }}>{rule.note}</div>}
                      <div style={{ fontSize: "0.7rem", color: "#9ca3af", marginTop: 4 }}>
                        {rule.hitCount} eşleşme{rule.lastHitAt ? ` · son: ${formatDate(rule.lastHitAt)}` : ""} · {rule.createdByName}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                      <button onClick={() => handleToggle(rule)} disabled={busyId === rule.id} title={rule.isEnabled ? "Pasifleştir" : "Aktifleştir"} style={{
                        width: 30, height: 30, borderRadius: 8, border: "1px solid #e5e7eb",
                        background: rule.isEnabled ? "#f0fdf4" : "#f9fafb", color: rule.isEnabled ? "#16a34a" : "#9ca3af",
                        cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        <i className={`bi ${rule.isEnabled ? "bi-toggle2-on" : "bi-toggle2-off"}`}></i>
                      </button>
                      {confirmDeleteId === rule.id ? (
                        <>
                          <button onClick={() => handleDelete(rule.id)} disabled={busyId === rule.id} style={{ padding: "0 10px", height: 30, borderRadius: 8, border: "none", background: "#dc2626", color: "#fff", fontWeight: 700, fontSize: "0.74rem", cursor: "pointer" }}>Sil</button>
                          <button onClick={() => setConfirmDeleteId(null)} style={{ padding: "0 10px", height: 30, borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff", color: "#374151", fontSize: "0.74rem", cursor: "pointer" }}>Vazgeç</button>
                        </>
                      ) : (
                        <button onClick={() => setConfirmDeleteId(rule.id)} title="Sil" style={{ width: 30, height: 30, borderRadius: 8, border: "1px solid #fecaca", background: "#fef2f2", color: "#dc2626", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <i className="bi bi-trash3"></i>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
